import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import type { BuzonNotificationIndicators } from "@/shared/types/interfaces/BuzonJudicial.interface";
import { BuzonTipoNotificacionEnum } from "@/shared/types/interfaces/BuzonJudicial.interface";
import { getNotificationTypeColor } from "@/modules/buzon-judicial/utils/buzonNotificacion.utils";

interface BuzonJudicialNoticeContentProps {
    welcome: string;
    fullName: string;
    totalPendientes: number;
    /** desglose de pendientes por tipo; null = solo se muestra el total */
    indicadores: BuzonNotificationIndicators | null;
}

const MAX_DISPLAY_COUNT = 99;

const formatCount = (count: number) => count > MAX_DISPLAY_COUNT ? `${MAX_DISPLAY_COUNT}+` : `${count}`;

/**
 * Contenido del toast de aviso de Buzón Judicial mostrado tras el post-login.
 */
const BuzonJudicialNoticeContent: FunctionComponent<BuzonJudicialNoticeContentProps> = ({ welcome, fullName, totalPendientes, indicadores }) => {
    const { t } = useTranslation();
    const { palette } = useTheme();

    // Los colores salen del mismo mapeo que los chips de la tabla del buzón
    const resumenPorTipo = indicadores
        ? [
            { tipo: BuzonTipoNotificacionEnum.DOCUMENTO, count: indicadores.notificacionDocumento, label: t('buzonJudicial.postLoginNotice.summary.documento', 'Documentos') },
            { tipo: BuzonTipoNotificacionEnum.CITACION, count: indicadores.notificacionCitacion, label: t('buzonJudicial.postLoginNotice.summary.citacion', 'Citaciones') },
            { tipo: BuzonTipoNotificacionEnum.CITACION_Y_DOCUMENTO, count: indicadores.notificacionCitacionDocumento, label: t('buzonJudicial.postLoginNotice.summary.citacionDocumento', 'Citación y documento') },
        ]
        : [];

    return (
        <div data-buzon-notice className="flex flex-col gap-3">
            <p className="text-[13px] font-semibold leading-snug text-[#1f2937]">
                {welcome}, <span className="font-bold">{fullName}</span>, {t('buzonJudicial.postLoginNotice.youHave', 'dispone de')}:
            </p>

            <div className="flex flex-col gap-2">
                <span className="text-sm font-bold text-[#1f2937]">
                    {t('buzonJudicial.postLoginNotice.totalPending', {
                        count: totalPendientes,
                        total: formatCount(totalPendientes),
                        defaultValue: totalPendientes === 1
                            ? '{{total}} pendiente de revisar en total'
                            : '{{total}} pendientes de revisar en total',
                    })}
                </span>
                {resumenPorTipo.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                        {resumenPorTipo.map(({ tipo, count, label }) => {
                            const color = palette[getNotificationTypeColor(tipo)].main;
                            return (
                                <div key={tipo} className="flex flex-col gap-1 rounded-lg bg-[#F8FAFC] px-2.5 py-2">
                                    <span className="text-2xl font-bold leading-none" style={{ color }}>{formatCount(count)}</span>
                                    <span className="flex items-start gap-1 text-[11px] leading-tight text-[#374151]">
                                        <span className="mt-1 size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                                        {label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Separador antes del botón "Abrir Buzón Judicial" que agrega sileo */}
            <div className="-mx-4 border-t border-[#e5e7eb]" />
        </div>
    );
};

export default BuzonJudicialNoticeContent;
